import { type ComponentPropsWithoutRef, type ReactElement } from 'react';
import cx from 'clsx';
import { type AvatarSize } from '../config';
import styles from './content-renderer.module.css';
import { getInitials } from './helpers/get-initials';

interface ContentRendererProps {
  content?: ReactElement;
  showInitials?: boolean;
  setImageError?: (error: boolean) => void;
  name: string;
  size: AvatarSize;
  props: ComponentPropsWithoutRef<'img'>;
}

/**
 * Renders the avatar’s inner content (either custom content, initials or an image)
 */
export function ContentRenderer({
  content,
  name,
  size,
  setImageError,
  showInitials,
  props,
}: ContentRendererProps): ReactElement {
  if (content) {
    return <div className={cx(styles['avatar-content'], props.className)}>{content}</div>;
  }

  if (showInitials) {
    const initials = getInitials(name, size);

    return (
      <span aria-hidden={props['aria-hidden']} className={props.className}>
        {initials}
      </span>
    );
  }

  const { alt, loading, className, ...propsRest } = props;

  const onError = (): void => {
    setImageError?.(true);
  };

  return (
    <img
      alt={alt ?? ''}
      className={cx(styles.avatar, className)}
      data-test-avatar
      loading={loading ?? 'eager'}
      onError={onError}
      {...propsRest}
    />
  );
}
